import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Badge } from 'react-bootstrap';
import { createProductWithNames, updateProductPrice, logError } from '../../api';
import ProductEditorForm from './ProductEditorForm';
import ScoreUpdateModal from './ScoreUpdateModal';
import './ProductEditor.css';

const ProductEditorModal = ({ show, onHide, onSave, product = null, mode = 'create' }) => {
    const [formData, setFormData] = useState({
        category: '',
        type: '',
        model: '',
        color: '',
        size: '',
        price: '',
        score: '',
        commissionPercentageModifier: 0,
        commissionFixedModifier: 0,
    });
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);
    const [originalData, setOriginalData] = useState(null);
    const [showScoreModal, setShowScoreModal] = useState(false);
    const [pendingScoreChange, setPendingScoreChange] = useState(null);
    const [showScoreStep, setShowScoreStep] = useState(false);

    
    // Estado para las opciones cargadas
    const [loadedOptions, setLoadedOptions] = useState({
        categories: [],
        types: [],
        models: [],
        colors: [],
        sizes: []
    });

    useEffect(() => {
        if (show) {
            setError(null);
            
            if (product && (mode === 'edit' || mode === 'duplicate')) {
                const data = {
                    category: product.category?.categoryName || '',
                    type: product.type?.typeName || '',
                    model: product.model?.modelName || '',
                    color: product.color?.colorName || '',
                    size: product.size?.size || '',
                    price: product.price || '',
                    score: product.score || '',
                    commissionPercentageModifier: product.commissionPercentageModifier || 0,
                    commissionFixedModifier: product.commissionFixedModifier || 0,
                };
                setFormData(data);
                setOriginalData(mode === 'edit' ? data : null);
                setHasChanges(mode === 'duplicate');
            } else {
                const emptyData = {
                    category: '',
                    type: '',
                    model: '',
                    color: '',
                    size: '',
                    price: '',
                    score: '',
                    commissionPercentageModifier: 0,
                    commissionFixedModifier: 0,
                };
                setFormData(emptyData);
                setOriginalData(null);
                setHasChanges(true);
            }
        }
    }, [show, product, mode]);

    useEffect(() => {
        if (originalData && mode === 'edit') {
            const changed = Object.keys(formData).some(key => 
                String(originalData[key]) !== String(formData[key])
            );
            setHasChanges(changed);
        }
    }, [formData, originalData, mode]);

    const handleFormChange = (newData) => {
        setFormData(newData);
    };



    const cleanPlaceholders = (data) => {
        if (mode === 'edit') {
            return data;
        }
        const cleaned = { ...data };
        Object.keys(cleaned).forEach(key => {
            if (cleaned[key] === '__PLACEHOLDER__') {
                cleaned[key] = '';
            }
        });
        return cleaned;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Check if score has changed in edit mode
        if (mode === 'edit' && originalData && 
            Number(formData.score) !== Number(originalData.score)) {
            setPendingScoreChange({
                oldScore: originalData.score || 0,
                newScore: Number(formData.score) || 0
            });
            setShowScoreStep(true);
            return;
        }
        
        await saveProduct();
    };
    
    const saveProduct = async () => {
        setLoading(true);
        setError(null);

        try {
            const cleanedData = cleanPlaceholders(formData);
            if (!cleanedData.category.trim() || cleanedData.category === '__PLACEHOLDER__') {
                throw new Error('La categoría es obligatoria');
            }
            if (!cleanedData.type.trim() || cleanedData.type === '__PLACEHOLDER__') {
                throw new Error('El tipo es obligatorio');
            }
            if (!cleanedData.model.trim() || cleanedData.model === '__PLACEHOLDER__') {
                throw new Error('El modelo es obligatorio');
            }
            
            const price = parseFloat(cleanedData.price);
            if (isNaN(price) || price <= 0) {
                throw new Error('El precio debe ser un número válido mayor que 0');
            }

            let result;
            
            if (mode === 'edit' && product) {
                // Obtener IDs de los dropdowns o usar el ID original si es texto libre
                const categoryId = loadedOptions.categories.find(c => c.categoryName === cleanedData.category)?.id || product.category?.id || -1;
                const typeId = loadedOptions.types.find(t => t.typeName === cleanedData.type)?.id || product.type?.id || -1;
                const modelId = loadedOptions.models.find(m => m.modelName === cleanedData.model)?.id || product.model?.id || -1;
                const colorId = loadedOptions.colors.find(c => c.colorName === cleanedData.color)?.id || product.color?.id || (cleanedData.color === '' ? null : -1);
                const sizeId = loadedOptions.sizes.find(s => s.size === cleanedData.size)?.id || product.size?.id || (cleanedData.size === '' ? null : -1);

                const productPrice = {
                    Id: product.id,
                    CategoryId: categoryId,
                    Category: { categoryName: cleanedData.category },
                    TypeId: typeId,
                    Type: { typeName: cleanedData.type },
                    ModelId: modelId,
                    Model: { modelName: cleanedData.model },
                    ColorId: colorId,
                    Color: { colorName: cleanedData.color },
                    SizeId: sizeId,
                    Size: { size: cleanedData.size },
                    Price: price,
                    Score: Number(cleanedData.score) || 0,
                    CommissionPercentageModifier: Number(cleanedData.commissionPercentageModifier),
                    CommissionFixedModifier: Number(cleanedData.commissionFixedModifier)
                };

                result = await updateProductPrice(product.id, productPrice);
            } else {
                const productDetails = {
                    categoryName: cleanedData.category,
                    typeName: cleanedData.type,
                    modelName: cleanedData.model,
                    colorName: cleanedData.color,
                    sizeName: cleanedData.size,
                    price,
                    score: Number(cleanedData.score) || 0,
                    PercentageModifier: Number(cleanedData.commissionPercentageModifier),
                    FixedModifier: Number(cleanedData.commissionFixedModifier),
                };

                result = await createProductWithNames(productDetails);
            }

            onSave(result.data);
            onHide();
        } catch (err) {
            logError(err);
            if (err.response?.status === 409) {
                setError(err.response.data.message || 'Ya existe un producto con esta combinación');
            } else {
                setError(err.message || 'Error al guardar el producto');
            }
        } finally {
            setLoading(false);
        }
    };
    
    const handleScoreModalHide = () => {
        setShowScoreStep(false);
        setPendingScoreChange(null);
    };
    
    const handleScoreModalApply = () => {
        setShowScoreStep(false);
        setPendingScoreChange(null);
        saveProduct();
    };
    
    const handleBackToEdit = () => {
        setShowScoreStep(false);
        setPendingScoreChange(null);
    };

    const getModalTitle = () => {
        switch (mode) {
            case 'edit': return 'Editar Producto';
            case 'duplicate': return 'Duplicar Producto';
            default: return 'Nuevo Producto';
        }
    };





    const getSubmitButtonText = () => {
        if (loading) return 'Guardando...';
        switch (mode) {
            case 'edit': return 'Guardar Cambios';
            case 'duplicate': return 'Crear Copia';
            default: return 'Crear Producto';
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" className="product-editor-modal">
            <Modal.Header closeButton className="bg-secondary text-white">
                <Modal.Title>
                    {showScoreStep ? 'Aplicar Cambio de Puntuación' : getModalTitle()}
                </Modal.Title>
            </Modal.Header>
            
            <Modal.Body className="product-editor-body">
                {error && (
                    <Alert variant="danger" className="mb-3">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        {error}
                    </Alert>
                )}

                {!showScoreStep ? (
                    <Form onSubmit={handleSubmit}>
                        <ProductEditorForm
                            formData={formData}
                            onChange={handleFormChange}
                            product={product}
                            mode={mode}
                            onOptionsLoad={(options) => {
                                setLoadedOptions(options);
                            }}
                        />
                    </Form>
                ) : (
                    <ScoreUpdateModal
                        product={product}
                        newScore={pendingScoreChange?.newScore}
                        oldScore={pendingScoreChange?.oldScore}
                        onApply={handleScoreModalApply}
                        onBack={handleBackToEdit}
                        embedded={true}
                    />
                )}
            </Modal.Body>
            
            <Modal.Footer className="product-editor-footer">
                {!showScoreStep ? (
                    <div className="d-flex justify-content-between w-100">
                        <div>
                            {mode === 'edit' && !hasChanges && (
                                <small className="text-muted">
                                    <i className="bi bi-info-circle me-1"></i>
                                    No hay cambios para guardar
                                </small>
                            )}
                        </div>
                        <div className="d-flex gap-2">
                            <Button variant="outline-secondary" onClick={onHide} disabled={loading}>
                                Cancelar
                            </Button>
                            <Button 
                                variant="primary" 
                                onClick={handleSubmit}
                                disabled={loading || (mode === 'edit' && !hasChanges) || 
                                         formData.category === '__PLACEHOLDER__' || 
                                         formData.type === '__PLACEHOLDER__' || 
                                         formData.model === '__PLACEHOLDER__'}
                                className="d-flex align-items-center gap-2"
                            >
                                {loading && <div className="spinner-border spinner-border-sm" role="status"></div>}
                                {getSubmitButtonText()}
                            </Button>
                        </div>
                    </div>
                ) : null}
            </Modal.Footer>
        </Modal>
    );
};

export default ProductEditorModal;